<template>
    <v-card elevation="0">
      <v-card-title v-if="props.showTitle">{{ mode }} Relationship</v-card-title>

      <v-form ref="form" v-model="formValid" v-if="formObject">
        <v-card-text>
          <v-select v-model="formObject.relationshipCategory"
                    :items="relationshipCategories"
                    label="Relationship Category (required)"
                    variant="outlined"
                    density="compact"
                    :rules="[requiredRule]"
                    class="mb-5"
                    data-mp-name="relationship-form.category"
                    persistent-hint
                    hint="This person is my ..."
          ></v-select>

          <v-text-field class="mb-5 mp-sensitive" density="compact" variant="outlined" v-model="formObject.relationshipType"
                        data-mp-name="relationship-form.type"
                        label="Relationship Type (required)" :rules="[requiredRule]"
                        persistent-hint
                        hint="e.g. husband, daughter, uncle, best friend, partner, mentor, boss, etc"></v-text-field>

          <v-text-field class="mb-5 mp-sensitive" density="compact" variant="outlined" v-model="formObject.nickName"
                        data-mp-name="relationship-form.nickname"
                        label="Nick Name"></v-text-field>

          <v-text-field class="mb-5 mp-sensitive" density="compact" variant="outlined" v-model="formObject.firstName"
                        data-mp-name="relationship-form.first-name"
                        label="First Name (required)" :rules="[requiredRule]"></v-text-field>

          <v-text-field class="mb-5 mp-sensitive" density="compact" variant="outlined" v-model="formObject.lastName"
                        data-mp-name="relationship-form.last-name"
                        label="Last Name (required)" :rules="[requiredRule]"></v-text-field>

          <v-select v-model="formObject.gender"
                    :items="genders"
                    label="Gender (required)"
                    variant="outlined"
                    density="compact"
                    :rules="[requiredRule]"
                    class="mb-5"
                    data-mp-name="relationship-form.gender"
          ></v-select>

          <v-text-field class="mb-5 mp-sensitive" density="compact" variant="outlined" v-model="formObject.dob"
                        data-mp-name="relationship-form.dob"
                        label="Birthday (required)" placeholder="mm-dd-yyyy" :rules="[requiredRule, dobRule]"></v-text-field>

          <v-select v-model="formObject.frequencyOfInteraction"
                    :items="frequencyOfInteractions"
                    label="Frequency Of Interaction"
                    variant="outlined"
                    density="compact"
                    class="mb-5"
                    data-mp-name="relationship-form.frequency"
                    persistent-hint
                    hint="How often do you interact?"
          ></v-select>

          <v-textarea class="mb-5 mp-sensitive" density="compact" variant="outlined" v-model="formObject.emotionalContext"
                      data-mp-name="relationship-form.emotional-context"
                      label="Emotional Context (required)"
                      :rules="[requiredRule]"
                      persistent-hint
                      hint="How does this relationship make you feel? e.g. “Supportive but strained,” “Feels distant,” “Brings comfort.”"></v-textarea>

          <v-textarea class="mb-5 mp-sensitive" density="compact" variant="outlined" v-model="formObject.currentChallenges"
                      data-mp-name="relationship-form.challenges"
                      label="Challenges / Growth Opportunities"
                      persistent-hint
                      hint="What challenges (if any) are you facing in this relationship? e.g. “We don’t communicate well,” “We argue about boundaries,” or “I feel like I’m not heard.”"></v-textarea>

          <v-textarea class="mb-5 mp-sensitive" density="compact" variant="outlined" v-model="formObject.traitsAndBehaviors"
                      data-mp-name="relationship-form.traits"
                      label="Traits & Behaviors"
                      persistent-hint
                      hint="What are some key traits or patterns you notice in this person? e.g. “Kind but forgetful,” “Often interrupts,” or “Playful and energetic.”"></v-textarea>

          <v-textarea class="mb-5 mp-sensitive" density="compact" variant="outlined" v-model="formObject.goals"
                      data-mp-name="relationship-form.goals"
                      label="Goals for the Relationship"
                      persistent-hint
                      hint="What would you like to improve or strengthen in this relationship? e.g. “Better communication,” “More understanding,” or “Repair trust.”"></v-textarea>

          <v-checkbox class="mb-5" density="compact" variant="outlined" v-model="formObject.hide"
                      label="Hide"
                      data-mp-name="relationship-form.hide"
                      persistent-hint
                      hint="This will hide the relationship from the sidebar."></v-checkbox>

        </v-card-text>
        <v-card-actions class="float-right">
          <v-btn variant="tonal" @click="handleSubmit"
                 :disabled="!formValid" :loading="loading" color="secondary"
                 data-mp-name="relationship-form.save">
            Save
          </v-btn>
          <v-btn v-if="showCancel" @click="$emit('update:cancelled')" :disabled="loading" data-mp-name="relationship-form.cancel">Cancel</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useKinectinStore } from '@/store/kinectin.js'
import eventBus from '@/service/EventBus.js'
import pushNotifications from '@/service/pushNotifications.js'
import { dobRule, genders, requiredRule, toBackendDobFormat, toFrontendDobFormat } from '@/service/uiUtils.js'

const emit = defineEmits(['update:cancelled', 'update:saved'])

const props = defineProps({
  relationship: { type: Object },
  showCancel: { type: Boolean },
  showTitle: { type: Boolean },
})

const store = useKinectinStore()

const mode = computed(() => props.relationship ? 'Edit' : 'Add')
const formObject = ref(null)
const formValid = ref(false)
const loading = ref(false)
const relationshipCategories = ['Family', 'Friend', 'Romantic', 'Child', 'Colleague', 'Other']
const frequencyOfInteractions = ['Daily', 'Weekly', 'Monthly', 'Rarely']
const hide = ref(false)

function syncFormObject() {
  formObject.value = props.relationship ? JSON.parse(JSON.stringify(props.relationship)) : {}
  formObject.value.dob = toFrontendDobFormat(formObject.value.dob)
}

watch(() => props.relationship, syncFormObject)

onMounted(() => {
  syncFormObject()
})

async function handleSubmit() {
  try {
    loading.value = true

    const attrs = JSON.parse(JSON.stringify(formObject.value))
    attrs.dob = toBackendDobFormat(attrs.dob)

    const handlers = { Add: handleAdd, Edit: handleEdit }
    await handlers[mode.value](attrs)
    emit('update:saved')
    eventBus.spa.emit('show-snackbar', { text: 'Saved!', color: 'green' })
    if (mode.value === 'Add') formObject.value = {}

  } catch (err) {
    eventBus.spa.emit('show-snackbar', { text: err, color: 'red' })
    console.error(err)
    throw err
  } finally {
    loading.value = false
  }
}

async function handleAdd(attrs) {
  await store.createRelationship({ attrs })
  await pushNotifications.track('Relationship Added')
}

async function handleEdit(attrs) {
  const relationshipId = attrs.relationshipId
  await store.updateRelationship({ relationshipId, attrs })
}
</script>
